import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import bookingService from '../../../../../../services/booking.service';
import { useEyeconxStore } from '../../../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../../../utils/ecxSwal';
import EcxTimePicker from '../../../../../common/EcxTimePicker';
import { formatDisplayTime, normalizeTimeToInterval } from '../../../../../common/ecxTimeUtils';
import { ClinicInfoPanel } from '../../info/clinicInfoParts';
import ClinicSettingsEmpty from '../../subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from '../../subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../../subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../../subparts/ClinicSettingsListSkeleton';
import {
    createEndTimeValidator,
    createLunchFromValidator,
    createLunchToValidator,
} from './doctorScheduleValidation';

const DAY_LABELS = {
    MONDAY: 'Monday',
    TUESDAY: 'Tuesday',
    WEDNESDAY: 'Wednesday',
    THURSDAY: 'Thursday',
    FRIDAY: 'Friday',
    SATURDAY: 'Saturday',
    SUNDAY: 'Sunday',
};

const formatDayName = (day) => DAY_LABELS[day] || day;

const formatTimeDisplay = (timeString) => {
    if (!timeString) return '—';
    return formatDisplayTime(timeString) || normalizeTimeToInterval(timeString);
};

const DoctorHours = ({ doctor }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [doctorHours, setDoctorHours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const { register, control, handleSubmit, reset, formState: { errors }, setValue, watch, getValues } = useForm({
        mode: 'onChange',
    });
    const watchIsOff = watch('isOff', false);

    useEffect(() => {
        if (doctor?.id) {
            fetchDoctorHours();
        }
    }, [doctor?.id, clinicId]);

    const fetchDoctorHours = async () => {
        setLoading(true);
        try {
            const res = await bookingService.getDoctorAvailability(clinicId, doctor.id);
            setDoctorHours(res.data || []);
        } catch (error) {
            console.log('Error fetching doctor hours:', error);
            showError({
                title: 'Load Failed',
                text: 'Unable to load doctor availability. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const updateDoctorHours = async (dayId, data) => {
        setIsSaving(true);
        try {
            const dayData = {
                id: dayId,
                clinicId,
                userId: doctor.id,
                day: data.day,
                isOff: data.isOff,
                startTime: data.isOff ? null : data.startTime,
                endTime: data.isOff ? null : data.endTime,
                lunchFrom: data.isOff ? null : data.lunchFrom,
                lunchTo: data.isOff ? null : data.lunchTo,
            };

            const res = await bookingService.updateDoctorAvailability(clinicId, doctor.id, dayData);
            setDoctorHours((prev) =>
                prev.map((day) =>
                    day.id === dayId
                        ? {
                            ...day,
                            ...res.data,
                            isOff: data.isOff,
                            startTime: data.startTime,
                            endTime: data.endTime,
                            lunchFrom: data.lunchFrom,
                            lunchTo: data.lunchTo,
                        }
                        : day
                )
            );
            setEditingId(null);
            reset();
            await showSuccess({
                title: 'Availability Updated',
                text: `${formatDayName(data.day)} was updated successfully.`,
            });
        } catch (error) {
            console.log('Error updating doctor hours:', error);
            showError({
                title: 'Update Failed',
                text: 'Unable to update availability. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = (day) => {
        setEditingId(day.id);
        setValue('day', day.day);
        setValue('isOff', day.isOff);
        setValue('startTime', normalizeTimeToInterval(day.startTime) || '09:00');
        setValue('endTime', normalizeTimeToInterval(day.endTime) || '17:00');
        setValue('lunchFrom', normalizeTimeToInterval(day.lunchFrom) || '12:00');
        setValue('lunchTo', normalizeTimeToInterval(day.lunchTo) || '13:00');
    };

    const handleCancel = () => {
        setEditingId(null);
        reset();
    };

    const renderEditForm = (day) => (
        <form
            onSubmit={handleSubmit((data) => updateDoctorHours(day.id, { ...data, day: day.day }))}
            className="doctor-availability-form"
        >
            <div className="doctor-availability-form__row">
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" {...register('isOff')} />
                    <label className="form-check-label small">Day off</label>
                </div>
            </div>
            {!watchIsOff && (
                <>
                    <div className="doctor-availability-form__row">
                        <div>
                            <label className="form-label small fw-semibold text-muted">Start</label>
                            <Controller
                                name="startTime"
                                control={control}
                                rules={{ required: watchIsOff ? false : 'Start time is required' }}
                                render={({ field }) => (
                                    <EcxTimePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Select start time"
                                        disabled={watchIsOff}
                                    />
                                )}
                            />
                            {errors.startTime && (
                                <div className="text-danger small mt-1">{errors.startTime.message}</div>
                            )}
                        </div>
                        <div>
                            <label className="form-label small fw-semibold text-muted">End</label>
                            <Controller
                                name="endTime"
                                control={control}
                                rules={{
                                    required: watchIsOff ? false : 'End time is required',
                                    validate: createEndTimeValidator(getValues),
                                }}
                                render={({ field }) => (
                                    <EcxTimePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Select end time"
                                        disabled={watchIsOff}
                                    />
                                )}
                            />
                            {errors.endTime && (
                                <div className="text-danger small mt-1">{errors.endTime.message}</div>
                            )}
                        </div>
                    </div>
                    <div className="doctor-availability-form__row">
                        <div>
                            <label className="form-label small fw-semibold text-muted">Lunch from</label>
                            <Controller
                                name="lunchFrom"
                                control={control}
                                rules={{ validate: createLunchFromValidator(getValues) }}
                                render={({ field }) => (
                                    <EcxTimePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Select lunch start"
                                        disabled={watchIsOff}
                                    />
                                )}
                            />
                            {errors.lunchFrom && (
                                <div className="text-danger small mt-1">{errors.lunchFrom.message}</div>
                            )}
                        </div>
                        <div>
                            <label className="form-label small fw-semibold text-muted">Lunch to</label>
                            <Controller
                                name="lunchTo"
                                control={control}
                                rules={{ validate: createLunchToValidator(getValues) }}
                                render={({ field }) => (
                                    <EcxTimePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Select lunch end"
                                        disabled={watchIsOff}
                                    />
                                )}
                            />
                            {errors.lunchTo && (
                                <div className="text-danger small mt-1">{errors.lunchTo.message}</div>
                            )}
                        </div>
                    </div>
                </>
            )}
            <ClinicSettingsFormFooter isSaving={isSaving} submitLabel="Save" />
        </form>
    );

    const panelActions = editingId ? (
        <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
    ) : null;

    if (loading) {
        return (
            <ClinicInfoPanel title="Weekly Availability" icon="bi-clock">
                <ClinicSettingsListSkeleton rows={7} />
            </ClinicInfoPanel>
        );
    }

    return (
        <ClinicInfoPanel title="Weekly Availability" icon="bi-clock" actions={panelActions}>
            {doctorHours.length === 0 ? (
                <ClinicSettingsEmpty
                    icon="bi-calendar-x"
                    title="No availability settings"
                    description="Availability settings will be created automatically."
                    actionLabel="Refresh"
                    onAction={fetchDoctorHours}
                />
            ) : (
                <div className="doctor-availability-list">
                    {doctorHours.map((day) => (
                        <div
                            key={day.id}
                            className={`doctor-availability-item ${editingId === day.id ? 'doctor-availability-item--editing' : ''}`}
                        >
                            {editingId === day.id ? (
                                <>
                                    <div className="doctor-availability-item__day">{formatDayName(day.day)}</div>
                                    <div className="doctor-availability-item__form">{renderEditForm(day)}</div>
                                </>
                            ) : (
                                <>
                                    <div className="doctor-availability-item__day">{formatDayName(day.day)}</div>
                                    <div className="doctor-availability-item__hours">
                                        <span className="doctor-availability-item__label">Hours</span>
                                        <span>
                                            {day.isOff
                                                ? '—'
                                                : `${formatTimeDisplay(day.startTime)} – ${formatTimeDisplay(day.endTime)}`}
                                        </span>
                                    </div>
                                    <div className="doctor-availability-item__lunch">
                                        <span className="doctor-availability-item__label">Lunch</span>
                                        <span>
                                            {day.isOff
                                                ? '—'
                                                : `${formatTimeDisplay(day.lunchFrom)} – ${formatTimeDisplay(day.lunchTo)}`}
                                        </span>
                                    </div>
                                    <span className={`clinic-settings-status-pill ${day.isOff ? 'is-closed' : 'is-open'}`}>
                                        {day.isOff ? 'Day Off' : 'Available'}
                                    </span>
                                    <div className="clinic-settings-record-item__actions">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary rounded-circle"
                                            onClick={() => handleEdit(day)}
                                            title="Edit"
                                        >
                                            <i className="bi bi-pencil"></i>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </ClinicInfoPanel>
    );
};

export default DoctorHours;
